import React from 'react';
import { LegalList, LegalNotice, LegalP, LegalPage, LegalSection } from '../components/Legal/LegalPage';

const Privacy: React.FC = () => {
  const updated = 'May 14, 2026';

  return (
    <LegalPage
      title="Privacy Policy"
      updated={updated}
      intro="RideSaathi is a driver-focused ride lead platform that aggregates and organizes ride requirements from authorized sources and verified contributors."
    >
      <LegalNotice>
        RideSaathi only processes ride-related information from authorized sources, verified contributors, connected ride information channels, ride lead submissions, and public or permitted ride requirements.
      </LegalNotice>

      <LegalSection title="1. Scope">
        <LegalP>
          This Privacy Policy explains how RideSaathi and Chahal Travel collect, use, store, and protect personal information when you use our website, driver app, admin tools, support channels, and related services in India.
        </LegalP>
        <LegalP>
          By using RideSaathi, you agree that we may process your information for the purposes described here and in our Terms and Conditions.
        </LegalP>
      </LegalSection>

      <LegalSection title="2. Information we process">
        <LegalList
          items={[
            'Account details such as name, phone number, email address, password hash, role, subscription status, and support history.',
            'Ride-related information such as pickup, drop, timing, vehicle preference, fare estimate, lead status, and contributor-provided contact details where applicable.',
            'Usage and device information such as IP address, browser, device type, pages visited, login time, and security logs.',
            'Payment and billing information such as order ID, invoice details, plan selected, payment status, and gateway response. RideSaathi does not store card, UPI PIN, or net banking credentials.',
            'Support, correction, opt-out, and deletion request details submitted by you through our forms, email, or in-app support.',
          ]}
        />
      </LegalSection>

      <LegalSection title="3. How ride lead information is handled">
        <LegalP>
          RideSaathi organizes ride requirements submitted through authorized sources, verified contributors, connected ride information channels, platform-generated rides, contributor-submitted rides, and public or permitted ride requirements.
        </LegalP>
        <LegalP>
          We aim to display only ride-related information needed for drivers to evaluate a lead. Contact details may be masked publicly and may only be visible to verified or logged-in users where applicable.
        </LegalP>
        <LegalP>
          If you believe any listing is incorrect, expired, unauthorized, or contains personal information that should not be displayed, you may request correction, removal, opt-out, or deletion.
        </LegalP>
      </LegalSection>

      <LegalSection title="4. How we use information">
        <LegalList
          items={[
            'To create and manage driver accounts, subscriptions, support requests, notifications, and ride lead access.',
            'To organize, deduplicate, classify, rank, and display ride requirements in a usable driver workspace.',
            'To mask or reveal contact details according to access rules, plan rules, safety checks, and verification status.',
            'To process payments, generate invoices, detect misuse, prevent fraud, and keep the platform secure.',
            'To review data correction, removal, deletion, opt-out, billing, and grievance requests.',
            'To comply with applicable Indian laws, lawful requests, tax, accounting, and security obligations.',
          ]}
        />
      </LegalSection>

      <LegalSection title="5. Sharing and service providers">
        <LegalP>
          We do not sell personal data or ride contact details as a standalone product. We may share limited information with service providers who help us operate RideSaathi, including hosting, payment, notification, analytics, customer support, security, and communication providers.
        </LegalP>
        <LegalP>
          We may disclose information where required by law, court order, government authority, payment dispute process, fraud prevention requirement, or to protect users and the platform.
        </LegalP>
      </LegalSection>

      <LegalSection title="6. Third-party platform disclaimer">
        <LegalP>
          RideSaathi does not claim partnership, sponsorship, endorsement, or affiliation with WhatsApp, Meta, Google, Apple, or any third-party platform unless officially stated in writing by RideSaathi and the relevant third party.
        </LegalP>
        <LegalP>
          Third-party services, payment gateways, app stores, browsers, maps, and communication platforms may have their own privacy policies and terms.
        </LegalP>
      </LegalSection>

      <LegalSection title="7. Platform disclaimer">
        <LegalP>
          RideSaathi is only a platform for organizing rides and does not guarantee ride booking, payment, safety, driver or customer verification, or completion of any ride.
        </LegalP>
      </LegalSection>

      <LegalSection title="8. Cookies and local storage" id="cookies">
        <LegalP>
          We use cookies and browser storage to keep you signed in, remember preferences, improve performance, measure aggregate usage, and show cookie consent choices. You can manage cookies through your browser settings, but some features may stop working correctly.
        </LegalP>
      </LegalSection>

      <LegalSection title="9. Security and retention">
        <LegalP>
          We use reasonable technical and organizational safeguards, including HTTPS, password hashing, access controls, authentication tokens, audit logs, and operational monitoring. No internet service can guarantee absolute security.
        </LegalP>
        <LegalP>
          We retain account, ride, payment, support, and audit information only for as long as needed for platform operation, legal compliance, dispute resolution, fraud prevention, accounting, and legitimate business purposes.
        </LegalP>
      </LegalSection>

      <LegalSection title="10. Your choices and requests">
        <LegalP>
          You may request access, correction, update, masking, removal, opt-out, or deletion of your information by using the Data Deletion / Opt-out page or contacting support.
        </LegalP>
        <LegalP>
          We may ask for reasonable verification before acting on a request. Some records may be retained where required for legal, tax, fraud prevention, security, dispute, or compliance reasons.
        </LegalP>
      </LegalSection>

      <LegalSection title="11. Children">
        <LegalP>
          RideSaathi is intended for users who are at least 18 years old and legally capable of using ride lead and transport-related services in India. We do not knowingly offer the platform to children.
        </LegalP>
      </LegalSection>

      <LegalSection title="12. Contact">
        <LegalP>
          For privacy questions, correction, removal, deletion, or grievance requests, contact us at <strong>support@ridesaathi.in</strong> or use the Contact / Grievance Officer page.
        </LegalP>
      </LegalSection>
    </LegalPage>
  );
};

export default Privacy;
